import { create } from "zustand";
import { persist } from "zustand/middleware";
import { CommingWith, VibeCategory } from "@/types/itinerary/staticOptions";
import getNextDate from "@/utils/getNextDay";

interface Destination {
  id: string;
  name: string;
  country: string;
}

type BudgetType = "ai" | "custom" | "none";

interface ItineraryState {
  destination: Destination;
  startedAt: string;
  endedAt: string;
  duration: number;
  commingWith: CommingWith | null;
  budget: number;
  budgetTerrance: number;
  budgetType: BudgetType;
  vibes: string[];
  idea: string;
  durationType: "days" | "custom";
  loadingPhrases: string[];
  continent: string;
  setContinent: (continent: string) => void;
  setDurationType: (durationType: "days" | "custom") => void;
  setLoadingPhrases: (loadingPhrases: string[]) => void;
  setBudgetType: (budgetType: BudgetType) => void;
  setDestination: (destination: Destination) => void;
  setDates: (startedAt: string, endedAt: string) => void;
  setDuration: (duration: number) => void;
  setCommingWith: (commingWith: CommingWith) => void;
  setBudget: (budget: number) => void;
  setBudgetTerrance: (budgetTerrance: number) => void;
  setVibes: (vibes: string[]) => void;
  setIdea: (idea: string) => void;
  reset: () => void;
}

type InitialState = Omit<
  ItineraryState,
  | "setDurationType"
  | "setLoadingPhrases"
  | "setBudgetType"
  | "setDestination"
  | "setDates"
  | "setDuration"
  | "setCommingWith"
  | "setBudget"
  | "setBudgetTerrance"
  | "setVibes"
  | "setIdea"
  | "setContinent"
  | "reset"
>;

const initialState: InitialState = {
  destination: {
    id: "",
    name: "",
    country: "",
  },
  startedAt: getNextDate(1),
  endedAt: getNextDate(2),
  duration: 3,
  commingWith: "Solo" as CommingWith,
  budget: 500,
  budgetTerrance: 33.33,
  budgetType: "none" as BudgetType,
  vibes: [],
  idea: "",
  durationType: "days" as const,
  loadingPhrases: [],
  continent: "",
};

export const useItinerarySteps = create<ItineraryState>()(
  persist(
    (set) => ({
      ...initialState,
      setVibes: (vibes) => set({ vibes }),
      setContinent: (continent) => set({ continent }),
      setDurationType: (durationType) => set({ durationType }),
      setLoadingPhrases: (loadingPhrases) => set({ loadingPhrases }),
      setIdea: (idea) => set({ idea }),
      setDestination: (destination) => set({ destination }),
      setDates: (startedAt, endedAt) => set({ startedAt, endedAt }),
      setDuration: (duration) => set({ duration }),
      setCommingWith: (commingWith) => set({ commingWith }),
      setBudget: (budget) => set({ budget }),
      setBudgetTerrance: (budgetTerrance) => set({ budgetTerrance }),
      setBudgetType: (budgetType) => set({ budgetType }),
      reset: () => set(initialState),
    }),
    {
      name: "itinerary-storage",
      version: 2,
    },
  ),
);
